import React, { useEffect, useMemo, useState } from "react";
import { api, money } from "../lib/api";
import { toast } from "sonner";
import {
  Plus, X, Pencil, Trash2, Loader2, Users, DollarSign, Receipt,
  CheckCircle2, XCircle, Wallet, Calendar, TrendingUp, Search, Award,
  History, KeyRound, Link2, FileText, ChevronLeft, ChevronRight,
} from "lucide-react";

const inputCls = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none";

const DEPARTMENTS = [
  { key: "field_ops", label: "Field Ops" },
  { key: "office", label: "Office" },
  { key: "management", label: "Management" },
  { key: "purchasing", label: "Purchasing" },
  { key: "finance", label: "Finance" },
  { key: "hr", label: "HR" },
  { key: "other", label: "Other" },
];

const EMPLOYMENT_TYPES = [
  { key: "salaried", label: "Salaried" },
  { key: "hourly", label: "Hourly" },
  { key: "subcontractor", label: "Subcontractor" },
];

const STATUS_COLORS = {
  active: "bg-emerald-100 text-emerald-800 border-emerald-200",
  on_leave: "bg-amber-100 text-amber-800 border-amber-200",
  terminated: "bg-slate-100 text-slate-500 border-slate-200",
};

const APPROVAL_COLORS = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  approved: "bg-blue-100 text-blue-800 border-blue-200",
  rejected: "bg-rose-100 text-rose-800 border-rose-200",
  paid: "bg-emerald-100 text-emerald-800 border-emerald-200",
};

const BONUS_TYPES = ["performance", "signing", "referral", "holiday", "other"];
const REIMB_CATEGORIES = ["travel", "meals", "supplies", "tools", "training", "mileage", "other"];

const emptyEmployee = {
  first_name: "", last_name: "", email: "", phone: "",
  employment_type: "salaried", status: "active", department: "field_ops",
  job_title: "", hire_date: new Date().toISOString().slice(0, 10),
  annual_salary: 0, salary_period: "annual",
  hourly_rate: 0, standby_rate: "", lunch_deduction: 0,
  contractor_rate: 0, contractor_rate_unit: "hour",
  address: "", ssn_last4: "",
  emergency_contact_name: "", emergency_contact_phone: "",
  notes: "", user_id: "",
};

// Salaried employees can be paid annually, monthly or weekly. The canonical
// storage field is always `annual_salary`; `salary_period` controls how the
// value is displayed / entered in the UI. Multipliers are used for both
// directions (annual ↔ per-period).
const PERIOD_LABELS = { annual: "Year", monthly: "Month", weekly: "Week" };
const PERIOD_TO_ANNUAL = { annual: 1, monthly: 12, weekly: 52 };
const annualFromPeriod = (value, period) => (Number(value) || 0) * (PERIOD_TO_ANNUAL[period] || 1);
const periodFromAnnual = (annual, period) => (Number(annual) || 0) / (PERIOD_TO_ANNUAL[period] || 1);

const errMsg = (e, fallback = "Failed") => {
  const d = e?.response?.data?.detail;
  if (!d) return fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join("; ");
  return JSON.stringify(d);
};

const compLabel = (e) => {
  if (e.employment_type === "salaried") {
    const period = e.salary_period || "annual";
    const display = periodFromAnnual(e.annual_salary || 0, period);
    const short = { annual: "yr", monthly: "mo", weekly: "wk" }[period] || "yr";
    return `${money(display)} / ${short}`;
  }
  if (e.employment_type === "hourly") return `${money(e.hourly_rate || 0)} / hr`;
  return `${money(e.contractor_rate || 0)} / ${e.contractor_rate_unit || "hour"}`;
};

export default function HR() {
  const [tab, setTab] = useState("employees");

  return (
    <div className="space-y-4 p-6" data-testid="hr-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 inline-flex items-center gap-2">
            <Users className="w-6 h-6" /> HR Management
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">Employees, salary history, bonuses, reimbursements and weekly/monthly payroll gross projection.</p>
        </div>
      </div>

      <div className="border-b border-slate-200 flex flex-wrap gap-x-4 gap-y-1 text-sm font-medium overflow-x-auto">
        {[
          { key: "employees", label: "Employees", icon: Users },
          { key: "bonuses", label: "Bonuses", icon: Award },
          { key: "reimbursements", label: "Reimbursements", icon: Receipt },
          { key: "payroll", label: "Payroll", icon: TrendingUp },
        ].map((t) => {
          const Ic = t.icon;
          return (
            <button
              key={t.key}
              data-testid={`hr-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-1.5 pb-2 px-1 -mb-px border-b-2 transition-colors whitespace-nowrap ${
                tab === t.key
                  ? "border-slate-900 text-slate-900"
                  : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              <Ic className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "employees" && <EmployeesTab />}
      {tab === "bonuses" && <BonusesTab />}
      {tab === "reimbursements" && <ReimbursementsTab />}
      {tab === "payroll" && <PayrollTab />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Employees
// ---------------------------------------------------------------------------
function EmployeesTab() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyEmployee);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [detailOpen, setDetailOpen] = useState(null);
  const [loginFor, setLoginFor] = useState(null); // employee getting a new system login
  const [importing, setImporting] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/hr/employees").then(setList).catch(() => setList([])).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openNew = () => { setEditing(null); setForm(emptyEmployee); setOpen(true); };
  const openEdit = (e) => {
    const period = e.salary_period || "annual";
    setEditing(e);
    setForm({
      ...emptyEmployee, ...e,
      salary_period: period,
      // Show the salary in the frequency the admin chose (converted from
      // stored annual value). On save() it will be re-annualized.
      annual_salary: periodFromAnnual(e.annual_salary || 0, period),
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) {
      toast.error("First and last name are required"); return;
    }
    setBusy(true);
    try {
      const period = form.salary_period || "annual";
      const payload = {
        ...form,
        // The UI collects the salary at the chosen period; the backend stores
        // it always annualized so payroll month = annual / 12 remains valid.
        annual_salary: annualFromPeriod(form.annual_salary, period),
        salary_period: period,
        hourly_rate: Number(form.hourly_rate) || 0,
        standby_rate: form.standby_rate === "" || form.standby_rate === null || form.standby_rate === undefined
          ? null
          : Number(form.standby_rate),
        lunch_deduction: Number(form.lunch_deduction) || 0,
        contractor_rate: Number(form.contractor_rate) || 0,
      };
      if (editing) {
        await api.patch(`/hr/employees/${editing.id}`, payload);
        toast.success("Employee updated");
      } else {
        await api.post("/hr/employees", payload);
        toast.success("Employee created");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e, "Failed to save")); }
    finally { setBusy(false); }
  };

  const remove = async (e) => {
    if (!window.confirm(`Delete "${e.name}"? This will delete their salary history, bonuses and reimbursements.`)) return;
    try {
      await api.del(`/hr/employees/${e.id}`);
      toast.success("Deleted");
      load();
    } catch (err) { toast.error(errMsg(err, "Failed to delete")); }
  };

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return list.filter((e) =>
      (statusFilter === "all" || e.status === statusFilter) &&
      (!t || e.name?.toLowerCase().includes(t) ||
        e.email?.toLowerCase().includes(t) ||
        e.employee_number?.toLowerCase().includes(t) ||
        e.job_title?.toLowerCase().includes(t))
    );
  }, [list, q, statusFilter]);

  const statusCount = (s) => list.filter((e) => e.status === s).length;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="hr-employee-search"
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, email, #, title..."
              className="rounded-lg border border-slate-300 pl-8 pr-3 py-1.5 text-sm w-64"
            />
          </div>
          <div className="flex gap-1">
            {[
              ["all", "All"], ["active", "Active"], ["on_leave", "On Leave"], ["terminated", "Terminated"],
            ].map(([k, l]) => (
              <button
                key={k} onClick={() => setStatusFilter(k)}
                data-testid={`hr-filter-${k}`}
                className={`text-[11px] font-bold px-2.5 py-1 rounded-full border capitalize ${statusFilter === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}
              >
                {l} {k !== "all" && `(${statusCount(k)})`}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="hr-employee-download-template-btn"
            onClick={() => api.downloadBlob("/hr/employees-import-template.xlsx", "employees-import-template.xlsx").catch(() => toast.error("Failed to download template"))}
            className="inline-flex items-center gap-1.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-bold px-3 py-2 rounded-lg"
          ><i className="fa-solid fa-download"></i> Download template</button>
          <label className="inline-flex items-center gap-1.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-bold px-3 py-2 rounded-lg cursor-pointer">
            {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <i className="fa-solid fa-upload"></i>}
            Import Excel
            <input
              type="file" accept=".xlsx" disabled={importing}
              onChange={async (evt) => {
                const file = evt.target.files?.[0]; evt.target.value = "";
                if (!file) return;
                setImporting(true);
                try {
                  const r = await api.postFile("/hr/employees-import-excel", file);
                  toast.success(`Imported ${r.imported}, skipped ${r.skipped}${r.errors?.length ? `, ${r.errors.length} errors` : ""}`);
                  load();
                } catch (e) { toast.error(errMsg(e, "Import failed")); }
                finally { setImporting(false); }
              }}
              className="hidden"
              data-testid="hr-employee-import-input"
            />
          </label>
          <button
            data-testid="hr-employee-new-btn"
            onClick={openNew}
            className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold px-4 py-2 rounded-lg"
          ><Plus className="w-4 h-4" /> New employee</button>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-xs text-slate-400 py-10"><Loader2 className="inline w-4 h-4 animate-spin mr-1" /> Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-xs text-slate-400 py-16 border-2 border-dashed border-slate-200 rounded-lg">
          {list.length === 0 ? "No employees yet — click New employee to add your first one." : "No employees match these filters."}
        </div>
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200 text-[10px] font-bold uppercase text-slate-500">
            <div className="col-span-1">#</div>
            <div className="col-span-3">Name</div>
            <div className="col-span-2">Title / Dept</div>
            <div className="col-span-1">Type</div>
            <div className="col-span-2 text-right">Compensation</div>
            <div className="col-span-1">Hire date</div>
            <div className="col-span-1">Status</div>
            <div className="col-span-1 text-right">Actions</div>
          </div>
          {filtered.map((e) => (
            <div key={e.id}
              className="grid grid-cols-12 gap-2 items-center px-3 py-2 border-b border-slate-100 text-xs hover:bg-slate-50/50"
              data-testid={`hr-employee-row-${e.id}`}
            >
              <div className="col-span-1 font-mono text-[10px] text-slate-500">{e.employee_number}</div>
              <div className="col-span-3 min-w-0">
                <button
                  data-testid={`hr-employee-open-${e.id}`}
                  onClick={() => setDetailOpen(e)}
                  className="font-semibold text-slate-900 truncate hover:underline text-left"
                >
                  {e.name}
                </button>
                <div className="text-[10px] text-slate-400 truncate">{e.email || "—"}</div>
              </div>
              <div className="col-span-2 min-w-0">
                <div className="truncate">{e.job_title || "—"}</div>
                <div className="text-[10px] text-slate-400 truncate capitalize">{DEPARTMENTS.find((d) => d.key === e.department)?.label || e.department}</div>
              </div>
              <div className="col-span-1 text-[10px] uppercase font-bold text-slate-500 capitalize">{e.employment_type}</div>
              <div className="col-span-2 text-right tabular-nums font-semibold">{compLabel(e)}</div>
              <div className="col-span-1 text-slate-500 text-[11px]">{e.hire_date || "—"}</div>
              <div className="col-span-1">
                <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border capitalize ${STATUS_COLORS[e.status] || STATUS_COLORS.terminated}`}>
                  {e.status.replace("_", " ")}
                </span>
              </div>
              <div className="col-span-1 text-right flex items-center justify-end gap-1">
                {e.user_id ? (
                  <span
                    data-testid={`hr-linked-${e.id}`}
                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200"
                    title="Linked to a system login"
                  >
                    <Link2 className="w-2.5 h-2.5" /> Linked
                  </span>
                ) : (
                  <button
                    data-testid={`hr-create-login-${e.id}`}
                    onClick={() => setLoginFor(e)}
                    className="p-1 rounded hover:bg-emerald-50 text-emerald-600"
                    title="Create system login for this employee"
                  ><KeyRound className="w-3.5 h-3.5" /></button>
                )}
                <button
                  data-testid={`hr-employee-edit-${e.id}`}
                  onClick={() => openEdit(e)}
                  className="p-1 rounded hover:bg-slate-100 text-slate-600"><Pencil className="w-3.5 h-3.5" /></button>
                <button
                  data-testid={`hr-employee-delete-${e.id}`}
                  onClick={() => remove(e)}
                  className="p-1 rounded hover:bg-red-50 text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <EmployeeModal
          form={form} setForm={setForm}
          editing={editing} busy={busy}
          onClose={() => setOpen(false)} onSave={save}
        />
      )}

      {detailOpen && (
        <EmployeeDetail
          employee={detailOpen}
          onClose={() => { setDetailOpen(null); load(); }}
          onEdit={(e) => { setDetailOpen(null); openEdit(e); }}
        />
      )}

      {loginFor && (
        <CreateLoginModal
          employee={loginFor}
          onClose={() => setLoginFor(null)}
          onCreated={() => { setLoginFor(null); load(); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Employee modal
// ---------------------------------------------------------------------------
function EmployeeModal({ form, setForm, editing, busy, onClose, onSave }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="hr-employee-modal">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 sticky top-0 bg-white z-10">
          <h2 className="text-base font-semibold inline-flex items-center gap-2">
            <Users className="w-4 h-4" /> {editing ? "Edit employee" : "New employee"}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-bold uppercase text-slate-500 mb-1">First name *</label>
              <input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                data-testid="hr-employee-first-name" className={inputCls} /></div>
            <div><label className="block text-xs font-bold uppercase text-slate-500 mb-1">Last name *</label>
              <input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                data-testid="hr-employee-last-name" className={inputCls} /></div>
            <div><label className="block text-xs font-bold uppercase text-slate-500 mb-1">Email</label>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} /></div>
            <div><label className="block text-xs font-bold uppercase text-slate-500 mb-1">Phone</label>
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} /></div>
            <div><label className="block text-xs font-bold uppercase text-slate-500 mb-1">Job title</label>
              <input value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} className={inputCls} placeholder="e.g. Senior Plumber" /></div>
            <div><label className="block text-xs font-bold uppercase text-slate-500 mb-1">Department</label>
              <select value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className={inputCls}>
                {DEPARTMENTS.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
              </select></div>
            <div><label className="block text-xs font-bold uppercase text-slate-500 mb-1">Employment type</label>
              <select
                data-testid="hr-employee-type"
                value={form.employment_type} onChange={(e) => setForm({ ...form, employment_type: e.target.value })} className={inputCls}>
                {EMPLOYMENT_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select></div>
            <div><label className="block text-xs font-bold uppercase text-slate-500 mb-1">Status</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls}>
                <option value="active">Active</option>
                <option value="on_leave">On leave</option>
                <option value="terminated">Terminated</option>
              </select></div>
            <div><label className="block text-xs font-bold uppercase text-slate-500 mb-1">Hire date</label>
              <input type="date" value={form.hire_date || ""} onChange={(e) => setForm({ ...form, hire_date: e.target.value })} className={inputCls} /></div>
            <div><label className="block text-xs font-bold uppercase text-slate-500 mb-1">Termination date</label>
              <input type="date" value={form.termination_date || ""} onChange={(e) => setForm({ ...form, termination_date: e.target.value })} className={inputCls} /></div>

            {/* Compensation */}
            <div className="col-span-2 border-t border-slate-100 pt-2 mt-1">
              <div className="text-[10px] font-bold uppercase text-slate-500 mb-1.5">Compensation (GROSS)</div>
            </div>
            {form.employment_type === "salaried" && (
              <div className="col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div className="col-span-2">
                  <label className="block text-xs font-bold uppercase text-slate-500 mb-1">
                    Salary ({PERIOD_LABELS[form.salary_period || "annual"]}) ($)
                  </label>
                  <input
                    data-testid="hr-employee-salary"
                    type="number" step="0.01" value={form.annual_salary}
                    onChange={(e) => setForm({ ...form, annual_salary: e.target.value })}
                    className={`${inputCls} text-right tabular-nums`} />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase text-slate-500 mb-1">Pay period</label>
                  <select
                    data-testid="hr-employee-salary-period"
                    value={form.salary_period || "annual"}
                    onChange={(e) => setForm({ ...form, salary_period: e.target.value })}
                    className={inputCls}
                  >
                    <option value="annual">Annual</option>
                    <option value="monthly">Monthly</option>
                    <option value="weekly">Weekly</option>
                  </select>
                </div>
                <p className="col-span-3 text-[10px] text-slate-500 -mt-1">
                  Equivalent annual: <b>{money(annualFromPeriod(form.annual_salary, form.salary_period || "annual"))}</b>
                  {" · "}monthly: <b>{money(annualFromPeriod(form.annual_salary, form.salary_period || "annual") / 12)}</b>
                  {" · "}weekly: <b>{money(annualFromPeriod(form.annual_salary, form.salary_period || "annual") / 52)}</b>
                </p>
              </div>
            )}
            {form.employment_type === "hourly" && (
              <div><label className="block text-xs font-bold uppercase text-slate-500 mb-1">Hourly rate ($/h)</label>
                <input
                  data-testid="hr-employee-hourly"
                  type="number" step="0.01" value={form.hourly_rate}
                  onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })}
                  className={`${inputCls} text-right tabular-nums`} /></div>
            )}
            {form.employment_type === "hourly" && (
              <div><label className="block text-xs font-bold uppercase text-slate-500 mb-1">Standby / Office rate ($/h)</label>
                <input
                  data-testid="hr-employee-standby"
                  type="number" step="0.01" value={form.standby_rate ?? ""}
                  placeholder="Same as hourly"
                  onChange={(e) => setForm({ ...form, standby_rate: e.target.value })}
                  className={`${inputCls} text-right tabular-nums`} />
                <p className="text-[10px] text-slate-400 mt-0.5">Blank = same as hourly rate.</p>
              </div>
            )}
            {form.employment_type === "hourly" && (
              <div className="col-span-2"><label className="block text-xs font-bold uppercase text-slate-500 mb-1">Lunch deduction</label>
                <select
                  data-testid="hr-employee-lunch"
                  value={form.lunch_deduction ?? 0}
                  onChange={(e) => setForm({ ...form, lunch_deduction: e.target.value })}
                  className={inputCls}>
                  <option value={0}>No deduction</option>
                  <option value={30}>30 min per shift</option>
                  <option value={60}>60 min per shift</option>
                </select>
                <p className="text-[10px] text-slate-400 mt-0.5">Minutes automatically deducted from every clock-in shift.</p>
              </div>
            )}
            {form.employment_type === "subcontractor" && (
              <>
                <div><label className="block text-xs font-bold uppercase text-slate-500 mb-1">Contractor rate ($)</label>
                  <input type="number" step="0.01" value={form.contractor_rate}
                    onChange={(e) => setForm({ ...form, contractor_rate: e.target.value })}
                    className={`${inputCls} text-right tabular-nums`} /></div>
                <div><label className="block text-xs font-bold uppercase text-slate-500 mb-1">Rate unit</label>
                  <select value={form.contractor_rate_unit} onChange={(e) => setForm({ ...form, contractor_rate_unit: e.target.value })} className={inputCls}>
                    <option value="hour">per hour</option>
                    <option value="day">per day</option>
                    <option value="contract">per contract</option>
                  </select></div>
              </>
            )}

            <div className="col-span-2 border-t border-slate-100 pt-2 mt-1">
              <div className="text-[10px] font-bold uppercase text-slate-500 mb-1.5">Personal (optional)</div>
            </div>
            <div className="col-span-2"><label className="block text-xs font-bold uppercase text-slate-500 mb-1">Address</label>
              <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputCls} /></div>
            <div><label className="block text-xs font-bold uppercase text-slate-500 mb-1">SSN (last 4)</label>
              <input value={form.ssn_last4} maxLength={4}
                onChange={(e) => setForm({ ...form, ssn_last4: e.target.value.replace(/\D/g, "") })}
                className={inputCls} placeholder="1234" /></div>
            <div />
            <div><label className="block text-xs font-bold uppercase text-slate-500 mb-1">Emergency contact name</label>
              <input value={form.emergency_contact_name} onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })} className={inputCls} /></div>
            <div><label className="block text-xs font-bold uppercase text-slate-500 mb-1">Emergency contact phone</label>
              <input value={form.emergency_contact_phone} onChange={(e) => setForm({ ...form, emergency_contact_phone: e.target.value })} className={inputCls} /></div>
            <div className="col-span-2"><label className="block text-xs font-bold uppercase text-slate-500 mb-1">Notes</label>
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows="2" className={inputCls} /></div>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200 sticky bottom-0 bg-white">
          <button onClick={onClose} disabled={busy} className="text-xs px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50">Cancel</button>
          <button
            data-testid="hr-employee-save-btn"
            onClick={onSave} disabled={busy}
            className="text-xs px-4 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-bold inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            {editing ? "Save changes" : "Create employee"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Create Login Modal — create a system user linked to an existing HR employee
// ---------------------------------------------------------------------------
const LOGIN_ROLES = [
  { key: "technician", label: "Technician" },
  { key: "pm", label: "Project Manager" },
  { key: "estimator", label: "Estimator" },
  { key: "purchasing", label: "Purchasing" },
  { key: "manager", label: "Manager" },
  { key: "financeiro", label: "Finance" },
  { key: "rh", label: "HR" },
  { key: "analista", label: "Analyst" },
  { key: "director", label: "Director" },
];

function makeTempPassword() {
  const alphabet = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
  let out = "";
  for (let i = 0; i < 10; i++) out += alphabet[Math.floor(Math.random() * alphabet.length)];
  return `Jmo${out}!`;
}

function CreateLoginModal({ employee, onClose, onCreated }) {
  const [email, setEmail] = useState(employee.email || "");
  const [password, setPassword] = useState(makeTempPassword());
  const [role, setRole] = useState("technician");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!email.trim()) { toast.error("Email is required"); return; }
    if (!password || password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      const created = await api.post("/users", {
        email: email.trim(),
        password,
        name: employee.name,
        role,
        phone: employee.phone || null,
        hr_employee_id: employee.id,
      });
      const w = created?._welcome_email;
      if (!w?.attempted) {
        toast.success(`Login created for ${employee.name}. ⚠️ Welcome email NOT sent (SendGrid not configured).`);
      } else if (w?.sent) {
        toast.success(`Login created and welcome email sent to ${email.trim()}`);
      } else {
        toast.warning(`Login created but welcome email failed: ${w?.error || "unknown"}. Use "Resend welcome" from /team.`, { duration: 10000 });
      }
      onCreated();
    } catch (e) { toast.error(errMsg(e, "Failed to create login")); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-white rounded-xl w-full max-w-md shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="hr-create-login-modal"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <h2 className="text-base font-semibold inline-flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-emerald-600" /> Create login
          </h2>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 text-xs">
            <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-800 mb-0.5">
              Employee
            </div>
            <div className="font-semibold text-emerald-900">{employee.name}</div>
            <div className="text-emerald-700 text-[11px]">
              {employee.employee_number} · {employee.job_title || employee.department} · {employee.employment_type}
              {employee.employment_type === "hourly" && employee.hourly_rate ? ` · ${money(employee.hourly_rate)}/h` : ""}
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase text-slate-500 mb-1">Login email *</label>
            <input
              data-testid="hr-login-email"
              type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              className={inputCls} placeholder="john.doe@jmogroupco.com"
            />
            {!employee.email && (
              <p className="text-[10px] text-amber-600 mt-1">
                This employee has no email on the HR record — the login email will not be saved back to HR automatically.
              </p>
            )}
          </div>

          <div>
            <label className="block text-xs font-bold uppercase text-slate-500 mb-1">Temporary password *</label>
            <div className="flex gap-2">
              <input
                data-testid="hr-login-password"
                value={password} onChange={(e) => setPassword(e.target.value)}
                className={`${inputCls} font-mono text-sm`}
              />
              <button
                type="button"
                onClick={() => setPassword(makeTempPassword())}
                className="text-[10px] font-bold px-2 rounded border border-slate-300 hover:bg-slate-50 whitespace-nowrap"
              >Regenerate</button>
            </div>
            <p className="text-[10px] text-slate-500 mt-1">
              The user will be required to change this on first login.
            </p>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase text-slate-500 mb-1">Role *</label>
            <select
              data-testid="hr-login-role"
              value={role} onChange={(e) => setRole(e.target.value)}
              className={inputCls}
            >
              {LOGIN_ROLES.map((r) => <option key={r.key} value={r.key}>{r.label} ({r.key})</option>)}
            </select>
            <p className="text-[10px] text-slate-500 mt-1">
              Compensation ({money(employee.hourly_rate || 0)}/h · lunch {employee.lunch_deduction || 0}min) will be pulled live from HR — the login form does not override it.
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200">
          <button
            onClick={onClose} disabled={busy}
            className="text-xs px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50"
          >Cancel</button>
          <button
            data-testid="hr-create-login-submit"
            onClick={submit} disabled={busy}
            className="text-xs px-4 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-bold inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}
            Create login
          </button>
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Employee Detail Drawer — Salary History + Raise flow
// ---------------------------------------------------------------------------
function EmployeeDetail({ employee, onClose, onEdit }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [raiseOpen, setRaiseOpen] = useState(false);
  const [raise, setRaise] = useState({
    effective_date: new Date().toISOString().slice(0, 10),
    employment_type: employee.employment_type,
    annual_salary: periodFromAnnual(employee.annual_salary || 0, employee.salary_period || "annual"),
    salary_period: employee.salary_period || "annual",
    hourly_rate: employee.hourly_rate || 0,
    contractor_rate: employee.contractor_rate || 0,
    contractor_rate_unit: employee.contractor_rate_unit || "hour",
    reason: "raise",
    notes: "",
  });
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/hr/employees/${employee.id}/salary-history`)
      .then(setHistory)
      .catch(() => setHistory([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, [employee.id]);

  const saveRaise = async () => {
    setBusy(true);
    try {
      await api.post(`/hr/employees/${employee.id}/salary-change`, {
        ...raise,
        annual_salary: annualFromPeriod(raise.annual_salary, raise.salary_period || "annual"),
        salary_period: raise.salary_period || "annual",
        hourly_rate: Number(raise.hourly_rate) || 0,
        contractor_rate: Number(raise.contractor_rate) || 0,
      });
      toast.success("Compensation change recorded");
      setRaiseOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e, "Failed to record")); }
    finally { setBusy(false); }
  };

  const compForEntry = (e) => {
    if (e.employment_type === "salaried") {
      const period = e.salary_period || "annual";
      const display = periodFromAnnual(e.annual_salary || 0, period);
      const short = { annual: "yr", monthly: "mo", weekly: "wk" }[period] || "yr";
      return `${money(display)} / ${short}`;
    }
    if (e.employment_type === "hourly") return `${money(e.hourly_rate)} / hr`;
    return `${money(e.contractor_rate)} / ${e.contractor_rate_unit}`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="hr-employee-detail">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-base font-semibold">{employee.name}</h2>
            <div className="text-[11px] text-slate-500">{employee.employee_number} · {employee.job_title || "No title"}</div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => onEdit(employee)} className="text-xs px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-1">
              <Pencil className="w-3.5 h-3.5" /> Edit
            </button>
            <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
          </div>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
            <div><div className="text-[10px] uppercase text-slate-500 font-bold">Type</div>
              <div className="font-semibold capitalize">{employee.employment_type}</div></div>
            <div><div className="text-[10px] uppercase text-slate-500 font-bold">Current comp</div>
              <div className="font-semibold">{compLabel(employee)}</div></div>
            <div><div className="text-[10px] uppercase text-slate-500 font-bold">Hire date</div>
              <div className="font-semibold">{employee.hire_date || "—"}</div></div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold inline-flex items-center gap-1.5"><History className="w-4 h-4" /> Salary history</div>
              <button
                data-testid="hr-raise-btn"
                onClick={() => setRaiseOpen(true)}
                className="text-[11px] font-bold px-2.5 py-1 rounded bg-slate-900 text-white hover:bg-slate-800 inline-flex items-center gap-1"
              ><Plus className="w-3 h-3" /> Record raise / change</button>
            </div>
            {loading ? (
              <div className="text-center text-xs text-slate-400 py-6"><Loader2 className="inline w-4 h-4 animate-spin" /></div>
            ) : history.length === 0 ? (
              <div className="text-xs text-slate-400 py-4 border border-dashed border-slate-200 rounded text-center">No history</div>
            ) : (
              <div className="border border-slate-200 rounded overflow-hidden">
                <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 border-b text-[10px] font-bold uppercase text-slate-500">
                  <div className="col-span-2">Effective</div>
                  <div className="col-span-2">Reason</div>
                  <div className="col-span-2">Type</div>
                  <div className="col-span-3 text-right">Comp</div>
                  <div className="col-span-3">Notes</div>
                </div>
                {history.map((h) => (
                  <div key={h.id} className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-slate-100 text-xs">
                    <div className="col-span-2 tabular-nums">{h.effective_date}</div>
                    <div className="col-span-2 capitalize">{h.reason}</div>
                    <div className="col-span-2 capitalize">{h.employment_type}</div>
                    <div className="col-span-3 text-right font-semibold tabular-nums">{compForEntry(h)}</div>
                    <div className="col-span-3 text-slate-500 truncate">{h.notes || "—"}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {raiseOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4 bg-black/50" onClick={() => setRaiseOpen(false)}>
            <div className="bg-white rounded-xl w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="hr-raise-modal">
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <h3 className="text-sm font-semibold">Record compensation change</h3>
                <button onClick={() => setRaiseOpen(false)} className="p-1 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
              </div>
              <div className="p-4 grid grid-cols-2 gap-3">
                <div><label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Effective date</label>
                  <input type="date" value={raise.effective_date} onChange={(e) => setRaise({ ...raise, effective_date: e.target.value })} className={inputCls} /></div>
                <div><label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Reason</label>
                  <select value={raise.reason} onChange={(e) => setRaise({ ...raise, reason: e.target.value })} className={inputCls}>
                    <option value="raise">Raise</option>
                    <option value="promotion">Promotion</option>
                    <option value="adjustment">Adjustment</option>
                    <option value="hire">Hire</option>
                  </select></div>
                <div className="col-span-2"><label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Employment type</label>
                  <select value={raise.employment_type} onChange={(e) => setRaise({ ...raise, employment_type: e.target.value })} className={inputCls}>
                    {EMPLOYMENT_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select></div>
                {raise.employment_type === "salaried" && (
                  <>
                    <div><label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">
                      New salary ({PERIOD_LABELS[raise.salary_period || "annual"]}) ($)
                    </label>
                      <input data-testid="hr-raise-salary" type="number" step="0.01" value={raise.annual_salary} onChange={(e) => setRaise({ ...raise, annual_salary: e.target.value })} className={`${inputCls} text-right tabular-nums`} /></div>
                    <div><label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Pay period</label>
                      <select
                        data-testid="hr-raise-period"
                        value={raise.salary_period || "annual"}
                        onChange={(e) => setRaise({ ...raise, salary_period: e.target.value })}
                        className={inputCls}
                      >
                        <option value="annual">Annual</option>
                        <option value="monthly">Monthly</option>
                        <option value="weekly">Weekly</option>
                      </select></div>
                    <p className="col-span-2 text-[10px] text-slate-500 -mt-1">
                      Annual: <b>{money(annualFromPeriod(raise.annual_salary, raise.salary_period || "annual"))}</b>
                      {" · "}Monthly: <b>{money(annualFromPeriod(raise.annual_salary, raise.salary_period || "annual") / 12)}</b>
                      {" · "}Weekly: <b>{money(annualFromPeriod(raise.annual_salary, raise.salary_period || "annual") / 52)}</b>
                    </p>
                  </>
                )}
                {raise.employment_type === "hourly" && (
                  <div className="col-span-2"><label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">New hourly rate ($/h)</label>
                    <input type="number" step="0.01" value={raise.hourly_rate} onChange={(e) => setRaise({ ...raise, hourly_rate: e.target.value })} className={`${inputCls} text-right tabular-nums`} /></div>
                )}
                {raise.employment_type === "subcontractor" && (
                  <>
                    <div><label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Rate ($)</label>
                      <input type="number" step="0.01" value={raise.contractor_rate} onChange={(e) => setRaise({ ...raise, contractor_rate: e.target.value })} className={`${inputCls} text-right tabular-nums`} /></div>
                    <div><label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Unit</label>
                      <select value={raise.contractor_rate_unit} onChange={(e) => setRaise({ ...raise, contractor_rate_unit: e.target.value })} className={inputCls}>
                        <option value="hour">per hour</option>
                        <option value="day">per day</option>
                        <option value="contract">per contract</option>
                      </select></div>
                  </>
                )}
                <div className="col-span-2"><label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Notes</label>
                  <input value={raise.notes} onChange={(e) => setRaise({ ...raise, notes: e.target.value })} className={inputCls} placeholder="e.g. Yearly review Jan 2026" /></div>
              </div>
              <div className="flex justify-end gap-2 px-4 py-3 border-t">
                <button onClick={() => setRaiseOpen(false)} className="text-xs px-3 py-1.5 rounded border border-slate-300">Cancel</button>
                <button
                  data-testid="hr-raise-save-btn"
                  onClick={saveRaise} disabled={busy}
                  className="text-xs px-4 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-bold disabled:opacity-50 inline-flex items-center gap-1"
                >{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Save</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bonuses Tab
// ---------------------------------------------------------------------------
function BonusesTab() {
  const [list, setList] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ employee_id: "", amount: 0, bonus_type: "performance", reason: "", period: "", notes: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/hr/bonuses"),
      api.get("/hr/employees?status=active"),
    ]).then(([b, e]) => { setList(b); setEmployees(e); })
      .catch(() => { setList([]); setEmployees([]); })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const save = async () => {
    if (!form.employee_id) { toast.error("Select an employee"); return; }
    if (!Number(form.amount)) { toast.error("Amount is required"); return; }
    setBusy(true);
    try {
      await api.post("/hr/bonuses", { ...form, amount: Number(form.amount) });
      toast.success("Bonus created (pending approval)");
      setOpen(false);
      setForm({ employee_id: "", amount: 0, bonus_type: "performance", reason: "", period: "", notes: "" });
      load();
    } catch (e) { toast.error(errMsg(e, "Failed")); }
    finally { setBusy(false); }
  };

  const act = async (id, action) => {
    try {
      if (action === "reject") {
        const reason = window.prompt("Reason for rejection:");
        if (reason === null) return;
        await api.post(`/hr/bonuses/${id}/reject`, { reason });
      } else {
        await api.post(`/hr/bonuses/${id}/${action}`, {});
      }
      toast.success(`Bonus ${action}d`);
      load();
    } catch (e) { toast.error(errMsg(e, "Failed")); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this bonus?")) return;
    try {
      await api.del(`/hr/bonuses/${id}`);
      load();
    } catch (e) { toast.error(errMsg(e, "Failed")); }
  };

  const filtered = statusFilter === "all" ? list : list.filter((b) => b.status === statusFilter);
  const statusCount = (s) => list.filter((b) => b.status === s).length;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-1 flex-wrap">
          {["all", "pending", "approved", "paid", "rejected"].map((k) => (
            <button
              key={k} onClick={() => setStatusFilter(k)}
              data-testid={`hr-bonus-filter-${k}`}
              className={`text-[11px] font-bold px-2.5 py-1 rounded-full border capitalize ${statusFilter === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}
            >{k} {k !== "all" && `(${statusCount(k)})`}</button>
          ))}
        </div>
        <button
          data-testid="hr-bonus-new-btn"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold px-4 py-2 rounded-lg"
        ><Plus className="w-4 h-4" /> New bonus</button>
      </div>

      {loading ? (
        <div className="text-center text-xs text-slate-400 py-10"><Loader2 className="inline w-4 h-4 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-xs text-slate-400 py-16 border-2 border-dashed border-slate-200 rounded-lg">
          {list.length === 0 ? "No bonuses yet." : "No bonuses match this filter."}
        </div>
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 border-b text-[10px] font-bold uppercase text-slate-500">
            <div className="col-span-3">Employee</div>
            <div className="col-span-2">Type / Period</div>
            <div className="col-span-3">Reason</div>
            <div className="col-span-1 text-right">Amount</div>
            <div className="col-span-1">Status</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {filtered.map((b) => (
            <div key={b.id} className="grid grid-cols-12 gap-2 items-center px-3 py-2 border-b border-slate-100 text-xs hover:bg-slate-50/50" data-testid={`hr-bonus-row-${b.id}`}>
              <div className="col-span-3 min-w-0">
                <div className="font-semibold truncate">{b.employee_name}</div>
                <div className="text-[10px] text-slate-400">{b.employee_number}</div>
              </div>
              <div className="col-span-2">
                <div className="capitalize">{b.bonus_type}</div>
                <div className="text-[10px] text-slate-400">{b.period || "—"}</div>
              </div>
              <div className="col-span-3 truncate text-slate-600">{b.reason || "—"}</div>
              <div className="col-span-1 text-right font-semibold tabular-nums">{money(b.amount)}</div>
              <div className="col-span-1">
                <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border capitalize ${APPROVAL_COLORS[b.status]}`}>{b.status}</span>
                {b.rejection_reason && <div className="text-[9px] text-rose-500 truncate mt-0.5">{b.rejection_reason}</div>}
              </div>
              <div className="col-span-2 text-right flex items-center justify-end gap-1">
                {b.status === "pending" && (
                  <>
                    <button data-testid={`hr-bonus-approve-${b.id}`} onClick={() => act(b.id, "approve")} className="p-1 rounded hover:bg-emerald-50 text-emerald-600"><CheckCircle2 className="w-4 h-4" /></button>
                    <button data-testid={`hr-bonus-reject-${b.id}`} onClick={() => act(b.id, "reject")} className="p-1 rounded hover:bg-rose-50 text-rose-600"><XCircle className="w-4 h-4" /></button>
                  </>
                )}
                {b.status === "approved" && (
                  <button data-testid={`hr-bonus-pay-${b.id}`} onClick={() => act(b.id, "pay")} className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-1">
                    <Wallet className="w-3 h-3" /> Mark paid
                  </button>
                )}
                <button onClick={() => remove(b.id)} className="p-1 rounded hover:bg-red-50 text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4 bg-black/50" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-xl w-full max-w-lg shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="hr-bonus-modal">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="text-sm font-semibold inline-flex items-center gap-2"><Award className="w-4 h-4" /> New bonus</h3>
              <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4 grid grid-cols-2 gap-3">
              <div className="col-span-2"><label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Employee</label>
                <select data-testid="hr-bonus-employee"
                  value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} className={inputCls}>
                  <option value="">— select —</option>
                  {employees.map((e) => <option key={e.id} value={e.id}>{e.name} ({e.employee_number})</option>)}
                </select></div>
              <div><label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Amount ($)</label>
                <input data-testid="hr-bonus-amount"
                  type="number" step="0.01" value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  className={`${inputCls} text-right tabular-nums`} /></div>
              <div><label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Type</label>
                <select value={form.bonus_type} onChange={(e) => setForm({ ...form, bonus_type: e.target.value })} className={inputCls}>
                  {BONUS_TYPES.map((k) => <option key={k} value={k}>{k}</option>)}
                </select></div>
              <div className="col-span-2"><label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Reason</label>
                <input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className={inputCls} placeholder="e.g. Q1 sales target" /></div>
              <div><label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Period</label>
                <input value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} className={inputCls} placeholder="e.g. 2026-01 or Q1-2026" /></div>
              <div><label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Notes</label>
                <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} /></div>
            </div>
            <div className="flex justify-end gap-2 px-4 py-3 border-t">
              <button onClick={() => setOpen(false)} className="text-xs px-3 py-1.5 rounded border border-slate-300">Cancel</button>
              <button data-testid="hr-bonus-save-btn"
                onClick={save} disabled={busy}
                className="text-xs px-4 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-bold disabled:opacity-50 inline-flex items-center gap-1"
              >{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reimbursements Tab
// ---------------------------------------------------------------------------
function ReimbursementsTab() {
  const [list, setList] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    employee_id: "", amount: 0, category: "travel", description: "",
    incurred_date: new Date().toISOString().slice(0, 10), receipt_b64: null, notes: "",
  });
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/hr/reimbursements"),
      api.get("/hr/employees?status=active"),
    ]).then(([b, e]) => { setList(b); setEmployees(e); })
      .catch(() => { setList([]); setEmployees([]); })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const onReceipt = (file) => {
    if (!file) return;
    const r = new FileReader();
    r.onload = () => setForm((p) => ({ ...p, receipt_b64: r.result }));
    r.readAsDataURL(file);
  };

  const save = async () => {
    if (!form.employee_id) { toast.error("Select an employee"); return; }
    if (!Number(form.amount)) { toast.error("Amount is required"); return; }
    if (!form.description.trim()) { toast.error("Description is required"); return; }
    setBusy(true);
    try {
      await api.post("/hr/reimbursements", { ...form, amount: Number(form.amount) });
      toast.success("Reimbursement submitted");
      setOpen(false);
      setForm({ employee_id: "", amount: 0, category: "travel", description: "",
        incurred_date: new Date().toISOString().slice(0, 10), receipt_b64: null, notes: "" });
      load();
    } catch (e) { toast.error(errMsg(e, "Failed")); }
    finally { setBusy(false); }
  };

  const act = async (id, action) => {
    try {
      if (action === "reject") {
        const reason = window.prompt("Reason for rejection:");
        if (reason === null) return;
        await api.post(`/hr/reimbursements/${id}/reject`, { reason });
      } else {
        await api.post(`/hr/reimbursements/${id}/${action}`, {});
      }
      toast.success(`Reimbursement ${action}d`);
      load();
    } catch (e) { toast.error(errMsg(e, "Failed")); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this reimbursement?")) return;
    try {
      await api.del(`/hr/reimbursements/${id}`);
      load();
    } catch (e) { toast.error(errMsg(e, "Failed")); }
  };

  const filtered = statusFilter === "all" ? list : list.filter((b) => b.status === statusFilter);
  const statusCount = (s) => list.filter((b) => b.status === s).length;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-1 flex-wrap">
          {["all", "pending", "approved", "paid", "rejected"].map((k) => (
            <button
              key={k} onClick={() => setStatusFilter(k)}
              data-testid={`hr-reimb-filter-${k}`}
              className={`text-[11px] font-bold px-2.5 py-1 rounded-full border capitalize ${statusFilter === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}
            >{k} {k !== "all" && `(${statusCount(k)})`}</button>
          ))}
        </div>
        <button
          data-testid="hr-reimb-new-btn"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold px-4 py-2 rounded-lg"
        ><Plus className="w-4 h-4" /> New reimbursement</button>
      </div>

      {loading ? (
        <div className="text-center text-xs text-slate-400 py-10"><Loader2 className="inline w-4 h-4 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-xs text-slate-400 py-16 border-2 border-dashed border-slate-200 rounded-lg">
          {list.length === 0 ? "No reimbursements yet." : "No reimbursements match this filter."}
        </div>
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 border-b text-[10px] font-bold uppercase text-slate-500">
            <div className="col-span-3">Employee</div>
            <div className="col-span-2">Category / Date</div>
            <div className="col-span-3">Description</div>
            <div className="col-span-1 text-right">Amount</div>
            <div className="col-span-1">Status</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {filtered.map((r) => (
            <div key={r.id} className="grid grid-cols-12 gap-2 items-center px-3 py-2 border-b border-slate-100 text-xs hover:bg-slate-50/50" data-testid={`hr-reimb-row-${r.id}`}>
              <div className="col-span-3 min-w-0">
                <div className="font-semibold truncate">{r.employee_name}</div>
                <div className="text-[10px] text-slate-400">{r.employee_number}</div>
              </div>
              <div className="col-span-2">
                <div className="capitalize">{r.category}</div>
                <div className="text-[10px] text-slate-400">{r.incurred_date || "—"}</div>
              </div>
              <div className="col-span-3 truncate text-slate-600">{r.description}</div>
              <div className="col-span-1 text-right font-semibold tabular-nums">{money(r.amount)}</div>
              <div className="col-span-1">
                <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border capitalize ${APPROVAL_COLORS[r.status]}`}>{r.status}</span>
                {r.rejection_reason && <div className="text-[9px] text-rose-500 truncate mt-0.5">{r.rejection_reason}</div>}
              </div>
              <div className="col-span-2 text-right flex items-center justify-end gap-1">
                {r.receipt_b64 && (
                  <a href={r.receipt_b64} target="_blank" rel="noreferrer" className="p-1 rounded hover:bg-slate-100 text-slate-500" title="View receipt"><Receipt className="w-3.5 h-3.5" /></a>
                )}
                {r.status === "pending" && (
                  <>
                    <button data-testid={`hr-reimb-approve-${r.id}`} onClick={() => act(r.id, "approve")} className="p-1 rounded hover:bg-emerald-50 text-emerald-600"><CheckCircle2 className="w-4 h-4" /></button>
                    <button data-testid={`hr-reimb-reject-${r.id}`} onClick={() => act(r.id, "reject")} className="p-1 rounded hover:bg-rose-50 text-rose-600"><XCircle className="w-4 h-4" /></button>
                  </>
                )}
                {r.status === "approved" && (
                  <button data-testid={`hr-reimb-pay-${r.id}`} onClick={() => act(r.id, "pay")} className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-1">
                    <Wallet className="w-3 h-3" /> Mark paid
                  </button>
                )}
                <button onClick={() => remove(r.id)} className="p-1 rounded hover:bg-red-50 text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4 bg-black/50" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-xl w-full max-w-lg shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="hr-reimb-modal">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="text-sm font-semibold inline-flex items-center gap-2"><Receipt className="w-4 h-4" /> New reimbursement</h3>
              <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4 grid grid-cols-2 gap-3">
              <div className="col-span-2"><label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Employee</label>
                <select data-testid="hr-reimb-employee"
                  value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} className={inputCls}>
                  <option value="">— select —</option>
                  {employees.map((e) => <option key={e.id} value={e.id}>{e.name} ({e.employee_number})</option>)}
                </select></div>
              <div><label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Amount ($)</label>
                <input data-testid="hr-reimb-amount"
                  type="number" step="0.01" value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  className={`${inputCls} text-right tabular-nums`} /></div>
              <div><label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Category</label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls}>
                  {REIMB_CATEGORIES.map((k) => <option key={k} value={k}>{k}</option>)}
                </select></div>
              <div><label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Incurred date</label>
                <input type="date" value={form.incurred_date} onChange={(e) => setForm({ ...form, incurred_date: e.target.value })} className={inputCls} /></div>
              <div><label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Receipt image</label>
                <input type="file" accept="image/*" onChange={(e) => onReceipt(e.target.files?.[0])} className="text-xs" />
                {form.receipt_b64 && <div className="text-[10px] text-emerald-600 mt-1">✓ Receipt attached</div>}
              </div>
              <div className="col-span-2"><label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Description</label>
                <input data-testid="hr-reimb-description"
                  value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} placeholder="What was this expense for?" /></div>
              <div className="col-span-2"><label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Notes</label>
                <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} /></div>
            </div>
            <div className="flex justify-end gap-2 px-4 py-3 border-t">
              <button onClick={() => setOpen(false)} className="text-xs px-3 py-1.5 rounded border border-slate-300">Cancel</button>
              <button data-testid="hr-reimb-save-btn"
                onClick={save} disabled={busy}
                className="text-xs px-4 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-bold disabled:opacity-50 inline-flex items-center gap-1"
              >{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Submit</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Monthly Payroll Summary
// ---------------------------------------------------------------------------
function PayrollTab() {
  const [mode, setMode] = useState("weekly");  // 'weekly' | 'monthly'
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  // Monday of the current week
  const mondayOf = (d) => {
    const x = new Date(d);
    x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
    x.setHours(0, 0, 0, 0);
    return x;
  };
  const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const [weekStart, setWeekStart] = useState(mondayOf(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const qs = mode === "weekly"
    ? `week_start=${fmtDate(weekStart)}`
    : `period=${period}`;

  const load = () => {
    setLoading(true);
    api.get(`/hr/payroll-summary?${qs}`)
      .then(setData)
      .catch(() => { toast.error("Failed to load"); setData(null); })
      .finally(() => setLoading(false));
  };
  useEffect(load, [mode, period, weekStart]);  // eslint-disable-line react-hooks/exhaustive-deps

  const exportCsv = () => {
    if (!data) return;
    const rows = [["#", "Employee", "Type", "Dept", "Hours", "Base", "Bonuses", "Reimb", "Total"]];
    data.rows.forEach((r) => rows.push([
      r.employee_number, r.name, r.employment_type, r.department,
      r.hours, r.base.toFixed(2), r.bonuses.toFixed(2), r.reimbursements.toFixed(2), r.total.toFixed(2),
    ]));
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const label = mode === "weekly" ? `week_${fmtDate(weekStart)}` : period;
    const a = document.createElement("a"); a.href = url; a.download = `payroll_${label}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const downloadWeeklyPdf = async () => {
    try {
      const blob = await api.getBlob(`/hr/payroll-summary/pdf?week_start=${fmtDate(weekStart)}`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `payroll-week-${fmtDate(weekStart)}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Failed to generate PDF"); }
  };

  const downloadPayslip = async (row) => {
    try {
      const blob = await api.getBlob(`/hr/payroll-summary/payslip.pdf?week_start=${fmtDate(weekStart)}&employee_id=${row.employee_id}`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `payslip-${row.employee_number || row.employee_id}-${fmtDate(weekStart)}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Failed to generate payslip"); }
  };

  const shiftWeek = (days) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + days);
    setWeekStart(mondayOf(d));
  };
  const weekEnd = new Date(weekStart); weekEnd.setDate(weekEnd.getDate() + 6);
  const isThisWeek = fmtDate(mondayOf(new Date())) === fmtDate(weekStart);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          {/* Mode toggle: Weekly / Monthly */}
          <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden">
            <button
              data-testid="hr-payroll-mode-weekly"
              onClick={() => setMode("weekly")}
              className={`text-xs font-bold px-3 py-1.5 ${mode === "weekly" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
            >Weekly</button>
            <button
              data-testid="hr-payroll-mode-monthly"
              onClick={() => setMode("monthly")}
              className={`text-xs font-bold px-3 py-1.5 ${mode === "monthly" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
            >Monthly</button>
          </div>
          {mode === "weekly" ? (
            <div className="flex items-center gap-1">
              <button data-testid="hr-week-prev" onClick={() => shiftWeek(-7)} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50"><ChevronLeft className="w-4 h-4 text-slate-600" /></button>
              <button data-testid="hr-week-this" onClick={() => setWeekStart(mondayOf(new Date()))} className={`px-3 py-2 rounded-lg text-xs font-medium border ${isThisWeek ? "border-slate-900 text-slate-900" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>This week</button>
              <button data-testid="hr-week-next" onClick={() => shiftWeek(7)} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50"><ChevronRight className="w-4 h-4 text-slate-600" /></button>
              <span className="ml-2 text-xs font-medium text-slate-600 inline-flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5 text-slate-400" />
                {weekStart.toLocaleDateString()} - {weekEnd.toLocaleDateString()}
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <label className="text-[10px] font-bold uppercase text-slate-500 inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Period</label>
              <input
                data-testid="hr-payroll-period"
                type="month" value={period} onChange={(e) => setPeriod(e.target.value)}
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm"
              />
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCsv} disabled={!data || loading}
            data-testid="hr-payroll-export"
            className="text-xs font-bold px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-1.5 disabled:opacity-50"
          ><DollarSign className="w-3.5 h-3.5" /> Export CSV</button>
          {mode === "weekly" && (
            <button onClick={downloadWeeklyPdf} disabled={!data || loading}
              data-testid="hr-payroll-weekly-pdf"
              className="text-xs font-bold px-3 py-1.5 rounded bg-orange-600 text-white hover:bg-orange-700 inline-flex items-center gap-1.5 disabled:opacity-50"
            ><FileText className="w-3.5 h-3.5" /> Weekly PDF</button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-center text-xs text-slate-400 py-10"><Loader2 className="inline w-4 h-4 animate-spin" /></div>
      ) : !data ? null : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="Base pay" value={money(data.totals.base)} color="text-slate-900" />
            <StatCard label="Bonuses" value={money(data.totals.bonuses)} color="text-blue-700" />
            <StatCard label="Reimbursements" value={money(data.totals.reimbursements)} color="text-purple-700" />
            <StatCard label="Total gross" value={money(data.totals.total)} color="text-emerald-700" bold />
          </div>

          {data.rows.length === 0 ? (
            <div className="text-center text-xs text-slate-400 py-16 border-2 border-dashed border-slate-200 rounded-lg">No active employees.</div>
          ) : (
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 border-b text-[10px] font-bold uppercase text-slate-500">
                <div className="col-span-1">#</div>
                <div className="col-span-3">Employee</div>
                <div className="col-span-1">Type</div>
                <div className="col-span-1 text-right">Hours</div>
                <div className="col-span-2 text-right">Base</div>
                <div className="col-span-1 text-right">Bonuses</div>
                <div className="col-span-1 text-right">Reimb</div>
                <div className="col-span-1 text-right">Total</div>
                <div className="col-span-1 text-right">Payslip</div>
              </div>
              {data.rows.map((r) => (
                <div key={r.employee_id} className="grid grid-cols-12 gap-2 items-center px-3 py-2 border-b border-slate-100 text-xs hover:bg-slate-50/50" data-testid={`hr-payroll-row-${r.employee_id}`}>
                  <div className="col-span-1 font-mono text-[10px] text-slate-500">{r.employee_number}</div>
                  <div className="col-span-3">
                    <div className="font-semibold">{r.name}</div>
                    <div className="text-[10px] text-slate-400 capitalize">{r.department}</div>
                  </div>
                  <div className="col-span-1 capitalize text-[11px]">{r.employment_type}</div>
                  <div className="col-span-1 text-right tabular-nums">{r.hours || "—"}</div>
                  <div className="col-span-2 text-right tabular-nums">{money(r.base)}</div>
                  <div className="col-span-1 text-right tabular-nums text-blue-700">{r.bonuses ? money(r.bonuses) : "—"}</div>
                  <div className="col-span-1 text-right tabular-nums text-purple-700">{r.reimbursements ? money(r.reimbursements) : "—"}</div>
                  <div className="col-span-1 text-right tabular-nums font-bold">{money(r.total)}</div>
                  <div className="col-span-1 text-right">
                    {mode === "weekly" && (
                      <button
                        data-testid={`hr-payslip-${r.employee_id}`}
                        onClick={() => downloadPayslip(r)}
                        title="Download payslip PDF"
                        className="p-1.5 rounded border border-slate-200 hover:bg-slate-100 text-slate-600"
                      ><FileText className="w-3.5 h-3.5" /></button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, color, bold }) {
  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-white">
      <div className="text-[10px] font-bold uppercase text-slate-500">{label}</div>
      <div className={`${bold ? "text-xl font-bold" : "text-lg font-semibold"} ${color} tabular-nums mt-0.5`}>{value}</div>
    </div>
  );
}
